import { useRef } from 'react';
import { Chip, GlitchText, Logo, SettingsBar, juice, sfx } from '../ui';
import './LanguageSelect.css';

/* ============================================================================
   LanguageSelect — the landing screen, styled as a cartridge slot.

   Each language is a physical cartridge the player slams into the machine.
   Picking one fires a burst from the card's own centre, so the feedback is
   anchored to what was actually clicked.
   ========================================================================== */

const CARTRIDGES = [
  {
    code: 'en',
    name: 'English',
    flag: '🇬🇧',
    blurb: 'en.wikipedia.org',
    tone: 'red',
    tilt: -2.5,
  },
  {
    code: 'fr',
    name: 'Français',
    flag: '🇫🇷',
    blurb: 'fr.wikipedia.org',
    tone: 'cyan',
    tilt: 2.5,
  },
];

function LanguageSelect({ onSelect }) {
  // Guards against a double-fire between the click and the article fetch —
  // two bursts and two loads for one press looks broken.
  const pickedRef = useRef(false);

  const handlePick = (event, code) => {
    if (pickedRef.current) return;
    pickedRef.current = true;

    const rect = event.currentTarget.getBoundingClientRect();
    juice.burst({
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
      count: 30,
      palette: code === 'en' ? 'fire' : 'cyan',
      power: 1.3,
    });
    juice.shake('medium');
    juice.flash('rgba(255, 31, 61, 0.28)', 260);
    sfx.play('select');

    onSelect(code);
  };

  return (
    <div className="lang">
      <div className="lang__masthead">
        <Logo size="lg" />
        <GlitchText as="p" className="lang__tagline" intensity="subtle" tone="cyan">
          GUESS THE HIDDEN WIKIPEDIA ARTICLE
        </GlitchText>
        <Chip tone="gold" icon="◆">
          One article. Every day.
        </Chip>
      </div>

      <div className="lang__slot">
        <span className="lang__slot-label u-label">Insert cartridge</span>

        <div className="lang__cards">
          {CARTRIDGES.map((cart) => (
            <button
              key={cart.code}
              type="button"
              className={`lang-card lang-card--${cart.tone}`}
              style={{ '--idle-tilt': `${cart.tilt}deg` }}
              onClick={(event) => handlePick(event, cart.code)}
              onMouseEnter={() => sfx.play('hover')}
            >
              <span className="lang-card__shine" aria-hidden="true" />
              <span className="lang-card__flag">{cart.flag}</span>
              <span className="lang-card__code">{cart.code.toUpperCase()}</span>
              <span className="lang-card__name">{cart.name}</span>
              <span className="lang-card__blurb">{cart.blurb}</span>
              <span className="lang-card__notches" aria-hidden="true">
                <i />
                <i />
                <i />
                <i />
              </span>
            </button>
          ))}
        </div>
      </div>

      <footer className="lang__footer">
        <SettingsBar />
      </footer>
    </div>
  );
}

export default LanguageSelect;
